import { PIPEDB_URL } from "./pipedb-url";
import { ClientData, LogEntry, MeasureData, Note, NoteInput, ObjectModelData, ShareLinkData, StarParams } from "./interfaces";
import { currentImpersonation } from "../api/impersonationRef";
import { mockClients, mockMeasurements, mockModels } from "./mock";
import { SessionData } from "./schemas";
import { readConsent } from './consent';
import { SendLinkError, type SendLinkKind, type SendLinkResult } from './dashboard/sendLink';

/**
 * The message behind a failed response.
 *
 * Every call site used to do `JSON.stringify(await res.json())`, but the API
 * returns plain text for errors — 'Bad Request', 'Unauthorized', 'height must
 * be between 100 and 250 cm'. So res.json() threw a SyntaxError and the user
 * saw "Unexpected token 'h'" instead of the reason. It rarely surfaced because
 * these endpoints rarely refused; validation on the API makes them refuse more
 * often and more usefully, so the message is worth keeping.
 */
const errorBody = async (res: Response): Promise<string> => {
    let text = '';
    try {
        text = await res.text();
    } catch {
        return `Request failed (${res.status})`;
    }
    if (!text) return `Request failed (${res.status})`;
    try {
        const parsed = JSON.parse(text);
        // Surface the common shapes rather than dumping the envelope.
        return typeof parsed === 'string'
            ? parsed
            : parsed?.message ?? parsed?.error ?? text;
    } catch {
        return text;
    }
};

/**
 * A refused request, with its status kept. Pages turn it into words a coach
 * can act on (lib/load-error.ts) rather than printing the server's body —
 * which for a 500 was whatever the function threw, e.g. "boom".
 */
export class HttpError extends Error {
    status: number;
    constructor(status: number, message: string) {
        super(message);
        this.name = 'HttpError';
        this.status = status;
    }
}

const httpError = async (res: Response): Promise<HttpError> =>
    new HttpError(res.status, await errorBody(res));


export const IS_MOCK = false;
export function getAuth(): AuthProvider | null {
    // Implement your logic to retrieve the AuthProvider instance
    return null;
}
export class AuthProvider {
    getAccessTokenSilently: () => Promise<string>;
    code?: string;
    profileID?: string;
    sessionID?: string;
    isCookieSet: boolean = false
    exchangeDonePromise: Promise<boolean> | null = null;
    anonymous: () => boolean = () => this.code !== undefined && this.code !== '';


    constructor(getAccessTokenSilently: () => Promise<string>, code?: string, profileID?: string, sessionID?: string, isCookieSet: boolean = false) {
        this.code = code;
        this.profileID = profileID;
        this.sessionID = sessionID;
        this.isCookieSet = isCookieSet;
        this.getAccessTokenSilently = getAccessTokenSilently;
    }
    /* The `exchangeCodeForCookie` function is responsible for exchanging a provided code for a cookie
    by making a POST request to a specific endpoint ("/api/auth/cookie"). Here is a breakdown of its
    functionality: */
    exchangeCodeForCookie = async (): Promise<boolean> => {
        if (this.isCookieSet) return true;
        // A dashboard's first requests go out together; they share one trade.
        if (!this.exchangeDonePromise) {
            this.exchangeDonePromise = this.tradeCode().catch((e) => {
                this.exchangeDonePromise = null;
                throw e;
            });
        }
        return this.exchangeDonePromise;
    };
    private tradeCode = async (): Promise<boolean> => {
        console.log("Exchanging code for cookie");
        if (!this.profileID) throw new Error("No profileID available");
        if (!this.code) throw new Error("No code available");
        const res = await fetch(`${PIPEDB_URL}/profiles/${this.profileID}/link/exchange`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: 'include',
            body: JSON.stringify({ code: this.code }),
        });
        if (!res.ok) throw new Error("Failed to exchange code for cookie");
        this.isCookieSet = true;
        return this.isCookieSet;
    };
    getAuthHeader = async () => {
        let headers = {
            'Content-Type': 'application/json',
        } as { [key: string]: string };
        if (this.code) {
            console.log("Waiting for code exchange to complete");
            if (!this.isCookieSet) {
                await this.exchangeCodeForCookie();
            }
            console.log("Code exchange completed, cookie should be set");
            headers = {
                ...headers,
            };
        } else if (this.isCookieSet) {
            // Cookie already set (e.g. from a previous scan session) — skip Auth0
            console.log("Using existing cookie for auth");
        } else {
            const token = await this.getAccessTokenSilently();
            if (token) {
                headers = {
                    ...headers,
                    Authorization: `Bearer ${token}`
                };
            }
        }
        if (currentImpersonation) {
            headers['X-Impersonate'] = currentImpersonation;
        }
        return headers;
    }
}


export const post = async (url: string, data: any, auth: AuthProvider) => {

    return fetch(`${PIPEDB_URL}/${url}`, {
        method: 'POST',
        headers: await auth.getAuthHeader(),
        credentials: 'include',
        body: JSON.stringify(data),
    });
}
export const put = async (url: string, data: any, auth: AuthProvider) => {

    return fetch(`${PIPEDB_URL}/${url}`, {
        method: 'PUT',
        headers: await auth.getAuthHeader(),
        credentials: 'include',
        body: JSON.stringify(data),
    });
}
export const get = async (url: string, auth: AuthProvider): Promise<Response> => {
    const headers = await auth.getAuthHeader();
    console.log("GET", `${PIPEDB_URL}/${url}`, headers);
    try {
        return await fetch(`${PIPEDB_URL}/${url}`, { headers: headers, credentials: 'include' });
    } catch (e: any) {
        // TODO improve UI error handling
        alert("Error fetching data try reloading page " + e.message);
        return new Response(JSON.stringify([]), {
            status: 500,
            headers: { 'Content-Type': 'application/json' }
        });
    }
}

export const del = async (url: string, auth: AuthProvider) => {
    const headers = await auth.getAuthHeader();
    return fetch(`${PIPEDB_URL}/${url}`, {
        method: 'DELETE',
        headers: await auth.getAuthHeader(),
        credentials: 'include'
    });
}
/* Get the current profile  */
export const getSession = async (profileID: string, sessionID: string, auth: AuthProvider): Promise<SessionData> => {
    if (IS_MOCK) {
        return {
            ID: sessionID,
            createdAt: new Date(),
            status: 'success',
            profileID: profileID
        };
    }
    const res = await get(`profiles/${profileID}/sessions/${sessionID}`, auth);

    return res.json();
}
export const getProfiles = async (auth: AuthProvider): Promise<ClientData[]> => {
    if (IS_MOCK) {
        return mockClients();
    }
    const res = await get('profiles', auth);
    // Was an unguarded res.json(): a 500 either threw a parser's complaint or,
    // with a JSON body, handed the roster an object that is not a list.
    if (!res.ok) throw await httpError(res);
    const body = await res.json();
    // A list or nothing: an envelope or error object here crashed the roster
    // with "filtered.map is not a function".
    if (!Array.isArray(body)) throw new HttpError(res.status, 'The client list was not a list.');
    return body;
}


export const addResource = async (profileID: string, sessionID: string, resourceName: string, data: any, auth: AuthProvider, raw = false) => {
    const res = await post(`profiles/${profileID}/sessions/${sessionID}/resources/${resourceName}`, data, auth);
    if (raw) {
        return res;
    }
    if (res.status !== 200) {
        throw new Error(await errorBody(res));
    }
    return res.json();
}

export const uploadImage = async (
    profileID: string,
    sessionID: string,
    view: string,
    imageDataUrl: string,
    gender: string,
    faceImg: boolean,
    auth: AuthProvider,
    capture?: object,
) => {
    const resp = await fetch(imageDataUrl);
    const blob = await resp.blob();
    const ext = blob.type === 'image/png' ? 'png' : 'jpg';

    const formData = new FormData();
    formData.append('image', blob, `${view}.${ext}`);
    formData.append('gender', gender);
    if (faceImg) formData.append('face_img', 'true');
    // SF-84 capture metadata; optional, the API stores it without blocking the upload.
    if (capture) formData.append('capture', JSON.stringify(capture));

    const headers = await auth.getAuthHeader();
    delete headers['Content-Type'];

    return fetch(`${PIPEDB_URL}/profiles/${profileID}/sessions/${sessionID}/upload/${view}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
    });
}
export const getResource = async (profileID: string, sessionID: string, resourceName: string, auth: AuthProvider, raw = false) => {
    const res = await get(`profiles/${profileID}/sessions/${sessionID}/resources/${resourceName}`, auth);
    if (raw) {
        return res;
    }
    if (res.status !== 200) {
        throw new Error(await errorBody(res));
    }
    return res.json();
}

export const addProfile = async (client: ClientData, auth: AuthProvider) => {
    const res = await post('profiles', client, auth);
    if (res.status !== 200) {
        throw new Error(await errorBody(res));
    }
    return res.json();
}
export const getProfile = async (id: string, auth: AuthProvider): Promise<ClientData> => {
    if (IS_MOCK) {
        return (await mockClients()).find(c => c.ID === id) as ClientData;
    }
    const res = await get(`profiles/${id}`, auth);
    // Was an unguarded res.json(). The API answers a missing profile with the
    // plain text 'Not Found', so the reader got `Unexpected token 'N', "Not
    // Found" is not valid JSON` — a parser's complaint standing in for the
    // reason. A 404 here means the id is not on this account, which is worth
    // saying in those words.
    if (res.status === 404) {
        throw new HttpError(404, 'No client with that id on this account.');
    }
    if (!res.ok) {
        throw await httpError(res);
    }
    return res.json();
}
export const updateProfile = async (id: string, data: Partial<ClientData>, auth: AuthProvider): Promise<ClientData> => {
    const res = await put(`profiles/${id}`, data, auth);
    if (res.status !== 200) {
        throw new Error(await errorBody(res));
    }
    return res.json();
}
export const initSession = async (client: ClientData, auth: AuthProvider): Promise<SessionData> => {
    // Guarded for the same reason as getProfile: a refusal arrives as text,
    // and parsing it as JSON reports a syntax error instead of the reason the
    // scan could not be started.
    const res = await post(`profiles/${client.ID}/sessions`, { client: client.ID }, auth);
    if (!res.ok) {
        throw new Error(await errorBody(res));
    }
    return (await res.json()) as SessionData;
}


/**
 * Everything a scan needs before the camera starts, sent as session resources.
 *
 * Each post is checked. They were not, so a refused one vanished: a weight
 * the API did not take became an unweighed scan that nobody could trace back
 * to the moment it happened. A refusal now stops the preflight and says which
 * part failed and why — the one point at which someone can still fix it.
 */
export const startTask = async (client: ClientData, weight: number | undefined, id: string, auth: AuthProvider) => {
    const send = async (name: string, body: unknown) => {
        const res = await post(`profiles/${client.ID}/sessions/${id}/resources/${name}`, body, auth);
        if (!res.ok) {
            throw new Error(`Could not start the scan (${name}): ${await errorBody(res)}`);
        }
    };
    if (!client.height) {
        throw new Error("Client height is undefined");
    }
    await send('front_proj', { scale: [2.0, 1.5], center: [0, 0] });
    await send('right_proj', { scale: [2.0, 1.5], center: [0, 0] });
    await send('height_input', { value: Number(client.height / 100) });
    // What the user agreed may be stored, recorded against the session that
    // produces the images. The API does not act on this yet — enforcement
    // belongs where the images land — but without sending it there is nothing
    // for it to act on, and no record of what was agreed at the time.
    await send('consent', await readConsent());
    await send('gender', { value: client.gender });
    if (weight !== undefined) {
        await send('weight', { value: weight });
    }
    return id;
}
/**
 * The share link for a profile, or undefined when there is none.
 *
 * Parsed only on success. It used to call res.json() unconditionally, so the
 * 404 that means "this client has never been shared" threw a SyntaxError on
 * "Not Found" during page load — an uncaught error for an ordinary state.
 */
export const getLink = async (
    profileID: string,
    auth: AuthProvider,
): Promise<ShareLinkData | undefined> => {
    const res = await get(`profiles/${profileID}/link/`, auth);
    if (res.status !== 200) return undefined;
    try {
        return (await res.json()) as ShareLinkData;
    } catch {
        return undefined;
    }
}
export const saveLink = async (profileID: string, link: ShareLinkData, auth: AuthProvider) => {
    const res = await post(`profiles/${profileID}/link/`, link, auth);
    if (res.status !== 200) {
        throw new Error(await errorBody(res));
    }
    return res.json();
}
export const saveTask = async (profileID: string, sessionID: string, measurements: MeasureData, auth: AuthProvider): Promise<SessionData> => {
    const res = await post(`profiles/${profileID}/sessions/${sessionID}/save`, measurements, auth);
    if (res.status !== 200) {
        throw new Error(await errorBody(res));
    }
    return res.json();
}
/** One entry per profile on the account, as GET /profile-summaries returns it. */
export interface ProfileSummary {
    ID: string;
    firstName: string;
    lastName: string;
    email: string;
    gender: 'male' | 'female';
    age?: number;
    height?: number;
    createdAt: string;
    lastMeasure: string | null;
    targetBodyFat: number | null;
    scanCount: number;
    first: ScanSummary | null;
    latest: ScanSummary | null;
}

export interface ScanSummary {
    createdAt: string;
    confirmed: boolean;
    measurements: Partial<MeasureData['measurements']> | null;
}

/**
 * The whole roster in one request.
 *
 * Returns undefined rather than throwing when the endpoint is not there, so a
 * caller can fall back to reading measurements per profile. That matters while
 * an older API is still deployed somewhere — the roster is slower without it,
 * not broken.
 */
export const getProfileSummaries = async (
    auth: AuthProvider,
): Promise<ProfileSummary[] | undefined> => {
    try {
        const res = await get('profile-summaries', auth);
        if (res.status !== 200) return undefined;
        return (await res.json()) as ProfileSummary[];
    } catch {
        return undefined;
    }
}

export const getMeasurements = async (profileID: string, auth: AuthProvider): Promise<MeasureData[]> => {
    const pageSize = 100;
    let page = 1;
    const allMeasurements: MeasureData[] = [];

    while (true) {
        const res = await get(`profiles/${profileID}/measurements?page=${page}&pageSize=${pageSize}`, auth);
        // Same guard as getProfile: errors come back as text, and parsing them
        // as JSON reports the wrong failure.
        if (!res.ok) {
            throw await httpError(res);
        }
        const measurementsPage = await res.json() as MeasureData[];
        allMeasurements.push(...measurementsPage);

        if (measurementsPage.length < pageSize) {
            break;
        }
        page += 1;
    }

    return allMeasurements.sort((a, b) => {
        const aTime = new Date(a.createdAt || 0).getTime();
        const bTime = new Date(b.createdAt || 0).getTime();
        return bTime - aTime;
    });
}
/**
 * Notes: their own collection, not a field on the measurement.
 *
 * A share reader gets the same GET and is answered with audience 'client'
 * only — the server's filter, not this code's, is what keeps coach notes
 * private. Every call throws the API's reason on refusal rather than parsing
 * its plain-text error as JSON.
 */
export const getNotes = async (profileID: string, auth: AuthProvider): Promise<Note[]> => {
    const res = await get(`profiles/${profileID}/notes`, auth);
    if (!res.ok) throw new Error(await errorBody(res));
    return res.json();
}
export const createNote = async (profileID: string, input: NoteInput, auth: AuthProvider): Promise<Note> => {
    const res = await post(`profiles/${profileID}/notes`, input, auth);
    if (!res.ok) throw new Error(await errorBody(res));
    return res.json();
}
export const updateNote = async (profileID: string, noteID: string, input: NoteInput, auth: AuthProvider): Promise<Note> => {
    const res = await fetch(`${PIPEDB_URL}/profiles/${profileID}/notes/${noteID}`, {
        method: 'PATCH',
        headers: await auth.getAuthHeader(),
        credentials: 'include',
        body: JSON.stringify(input),
    });
    if (!res.ok) throw new Error(await errorBody(res));
    return res.json();
}
export const deleteNote = async (profileID: string, noteID: string, auth: AuthProvider): Promise<void> => {
    const res = await del(`profiles/${profileID}/notes/${noteID}`, auth);
    if (!res.ok) throw new Error(await errorBody(res));
}
export const getMeasurement = async (profileID: string, measurementID: string, auth: AuthProvider): Promise<MeasureData | null> => {
    const res = await get(`profiles/${profileID}/measurements/${measurementID}`, auth);
    if (res.status === 404) return null;
    if (!res.ok) return null;
    return res.json();
}
export const patchMeasurement = async (profileID: string, measurementId: string, data: Partial<MeasureData>, auth: AuthProvider): Promise<MeasureData> => {
    const res = await fetch(`${PIPEDB_URL}/profiles/${profileID}/measurements/${measurementId}`, {
        method: 'PATCH',
        headers: await auth.getAuthHeader(),
        body: JSON.stringify(data),
    });
    if (res.status !== 200) {
        throw new Error(await errorBody(res));
    }
    return res.json();
}
export const deleteMeasurement = async (profileID: string, measurementId: string, auth: AuthProvider): Promise<Response> => {
    const res = await del(`profiles/${profileID}/measurements/${measurementId}`, auth);
    if (res.status !== 200) {
        throw new Error(await errorBody(res));
    }
    return res;
}
export const deleteClient = async (profileID: string, auth: AuthProvider): Promise<Response> => {
    const res = await del(`profiles/${profileID}`, auth);
    if (res.status !== 200) {
        throw new Error(await errorBody(res));
    }
    return res;
}

export const getModels = async (profileID: string, auth: AuthProvider): Promise<ObjectModelData[]> => {
    const res = await get(`models/${profileID}/`, auth);
    return res.json();
}
export const getModel = async (profileID: string, taskId: string, auth: AuthProvider): Promise<String> => {

    const res = await get(`profiles/${profileID}/measurements/${taskId}/model`, auth);
    return res.text();
}
export const getStarParams = async (profileID: string, taskId: string, auth: AuthProvider): Promise<StarParams> => {
    const res = await get(`models/${profileID}/${taskId}_params.json`, auth);
    return res.json();
}
export const generateModel = async (profileID: string, sessionID: string, percentage: number, auth: AuthProvider) => {
    const res = await get(`profiles/${profileID}/measurements/${sessionID}/generate?bodyFat=${percentage}`, auth);
    if (res.status !== 200) {
        throw new Error(await errorBody(res));
    }
    return res.json();
}
export const setTarget = async (clientId: string, sessionID: string, target: MeasureData['measurements'], auth: AuthProvider) => {
    const res = await post(`profiles/${clientId}/${sessionID}/target`, target, auth);
    if (res.status !== 200) {
        throw new Error(await errorBody(res));
    }
    return res
}




export const postLog = async (data: LogEntry, auth: AuthProvider) => {
    const res = await post('log', data, auth);
    if (res.status !== 200) {
        console.error(await res.text());
    }
    return
}
export const onShare = async (enabled: boolean, clientId: string, auth: AuthProvider): Promise<ShareLinkData> => {
    const shareLinkData = {
        ID: '',
        account: '',
        profileID: '',
        createdAt: new Date(),
        code: '',
        active: enabled

    } as ShareLinkData;

    const link = await saveLink(clientId, shareLinkData, auth);
    console.log("Share link saved: ", link);
    return link
}

/**
 * Email the client their scan or dashboard link (SF-91). The server builds the
 * link — a fresh session for 'scan' — and sends it only to the profile's own
 * address; `origin` is so the link points at the deployment the coach is on.
 * Refusals throw a SendLinkError carrying the status, which the UI turns into
 * copy. A network failure is status 0.
 */
export const sendLinkEmail = async (
    profileID: string,
    kind: SendLinkKind,
    auth: AuthProvider,
): Promise<SendLinkResult> => {
    let res: Response;
    try {
        res = await post(`profiles/${profileID}/send-link`, { kind, origin: window.location.origin }, auth);
    } catch {
        throw new SendLinkError(0);
    }
    if (!res.ok) throw new SendLinkError(res.status);
    return res.json();
}
